import React from 'react';
import { Box, Grid, Paper, Stack, Tooltip, Typography } from '@mui/material';
import {
  Event,
  Inventory2,
  ShowChart,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
} from '@mui/icons-material';
import type { ArticuloInfo360Indicadores } from '../types';
import { formatCantidad, resolveUnidadMedida } from '../formatCantidad';

interface Props {
  data: ArticuloInfo360Indicadores;
}

interface KpiItem {
  title: string;
  value: string;
  helper: string;
  icon: React.ReactNode;
  tone: 'ok' | 'warning' | 'danger' | 'neutral';
  tooltip: string;
  showTrend?: boolean;
}

const toneColor = (tone: KpiItem['tone']): string => {
  if (tone === 'ok') return '#2e7d32';
  if (tone === 'warning') return '#ed6c02';
  if (tone === 'danger') return '#d32f2f';
  return '#455a64';
};

const ArticuloInfo360KpiRow: React.FC<Props> = ({ data }) => {
  const um = resolveUnidadMedida(data.unidadMedida);
  const conDemanda =
    data.ventas12MesesCantidad > 0
    || data.demandaMensual6m > 0
    || data.demandaPromedio12m > 0;

  const stockTone: KpiItem['tone'] =
    data.stockNeto < 0 ? 'danger'
      : data.stockNeto <= 0 && conDemanda ? 'danger'
        : data.stockNeto <= 0 ? 'neutral'
          : data.stockMin > 0 && data.stockNeto < data.stockMin && conDemanda ? 'warning'
            : data.stockMin > 0 && data.stockNeto < data.stockMin ? 'neutral'
              : 'ok';

  const stockHelper =
    data.stockNeto < 0 ? 'Crítico'
      : data.stockNeto <= 0 && !conDemanda ? 'Sin demanda'
        : data.stockNeto <= 0 ? 'Crítico'
          : stockTone === 'warning' ? 'Bajo mínimo'
            : stockTone === 'ok' ? 'Disponible'
              : 'En cero';

  const coberturaTone: KpiItem['tone'] =
    data.coberturaDias <= 0 && data.demandaMensual6m > 0 ? 'danger'
      : data.coberturaDias < data.leadTimeDias && conDemanda ? 'warning'
        : data.coberturaDias <= 0 ? 'neutral'
          : 'ok';

  const tendenciaIcon =
    data.tendenciaVentasPct != null && data.tendenciaVentasPct > 3
      ? <TrendingUp fontSize="small" aria-hidden />
      : data.tendenciaVentasPct != null && data.tendenciaVentasPct < -3
        ? <TrendingDown fontSize="small" aria-hidden />
        : <TrendingFlat fontSize="small" aria-hidden />;

  const ventaTone: KpiItem['tone'] =
    data.diasDesdeUltimaVenta == null ? 'neutral'
      : data.diasDesdeUltimaVenta > 90 ? 'danger'
        : data.diasDesdeUltimaVenta > 60 ? 'warning'
          : 'ok';

  const items: KpiItem[] = [
    {
      title: 'Stock neto',
      value: formatCantidad(data.stockNeto, um),
      helper: stockHelper,
      icon: <Inventory2 fontSize="small" aria-hidden />,
      tone: stockTone,
      tooltip: `Disponible venta ${formatCantidad(data.stockDisponibleVenta, um)} · Reservado ${formatCantidad(data.stockReservado, um)} · OC ${formatCantidad(data.stockPendienteOc, um)}`,
    },
    {
      title: 'Cobertura',
      value: data.coberturaDias >= 9999 ? 'Sin demanda' : `${Math.round(data.coberturaDias)} días`,
      helper: coberturaTone === 'ok' ? 'Adecuada'
        : coberturaTone === 'warning' ? 'Baja'
          : coberturaTone === 'danger' ? 'Sin stock'
            : 'Sin demanda',
      icon: <Event fontSize="small" aria-hidden />,
      tone: coberturaTone,
      tooltip: `Lead time ${Math.round(data.leadTimeDias)} días · Demanda mensual ${formatCantidad(data.demandaMensual6m, um)}`,
    },
    {
      title: 'Ventas 12 meses',
      value: formatCantidad(data.ventas12MesesCantidad, um),
      helper: data.tendenciaVentasPct == null
        ? 'Sin período comparable'
        : `${data.tendenciaVentasPct > 0 ? '+' : ''}${data.tendenciaVentasPct}% tendencia`,
      icon: <ShowChart fontSize="small" aria-hidden />,
      tone: data.tendenciaVentasPct != null && data.tendenciaVentasPct < -15 ? 'warning' : 'neutral',
      tooltip: `Importe ${data.ventas12MesesImporte.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 })}`,
      showTrend: true,
    },
    {
      title: 'Última venta',
      value: data.diasDesdeUltimaVenta == null
        ? 'Sin ventas'
        : data.diasDesdeUltimaVenta === 0 ? 'Hoy' : `${data.diasDesdeUltimaVenta} días`,
      helper: data.diasDesdeUltimaVenta == null
        ? 'Sin historial'
        : ventaTone === 'ok' ? 'Reciente' : 'Poco frecuente',
      icon: <ShowChart fontSize="small" aria-hidden />,
      tone: ventaTone,
      tooltip: 'Días transcurridos desde la última venta registrada.',
    },
  ];

  return (
    <Grid container spacing={1.5}>
      {items.map((item) => (
        <Grid item xs={12} sm={6} md={3} key={item.title}>
          <Tooltip title={item.tooltip} arrow>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                height: '100%',
                borderColor: toneColor(item.tone),
                borderWidth: 1,
              }}
            >
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ color: toneColor(item.tone), display: 'flex' }}>{item.icon}</Box>
                  <Typography variant="caption" color="text.secondary">
                    {item.title}
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {item.value}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {item.showTrend ? (
                    <Box sx={{ color: toneColor(item.tone), display: 'flex' }}>{tendenciaIcon}</Box>
                  ) : null}
                  <Typography
                    variant="caption"
                    sx={{ color: toneColor(item.tone), fontWeight: 600 }}
                  >
                    {item.helper}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Tooltip>
        </Grid>
      ))}
    </Grid>
  );
};

export default ArticuloInfo360KpiRow;
